import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft, Check, Clock, User } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventUser {
  token: string
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  gender: string | null
  country: string | null
  city: string | null
  age: string | null
  title: string | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  approved: boolean | null
  imageUrl: string | null
}

interface CustomField {
  name: string
  value: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  eventUser: EventUser
  registrationFields: string[]
  customFields: CustomField[]
  urls: AppShellProps['urls'] & {
    approve: string
    eventUsers: string
  }
  translations: AppShellProps['translations'] & {
    userDetails: string
    approve: string
    back: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    title?: string
    gender: string
    country: string
    city: string
    age: string
    idNumber: string
    passportNumber: string
    bloodType: string
    approved: string
    pending: string
  }
}

/* ------------------------------------------------------------------ */
/*  Field label mapping                                                */
/* ------------------------------------------------------------------ */

const FIELD_MAP: Record<string, { key: keyof EventUser; tKey: string }> = {
  title:           { key: 'title',          tKey: 'title'          },
  first_name:      { key: 'firstName',      tKey: 'firstName'      },
  last_name:       { key: 'lastName',       tKey: 'lastName'       },
  phone_number:    { key: 'phoneNumber',    tKey: 'phoneNumber'    },
  email:           { key: 'email',          tKey: 'email'          },
  organization:    { key: 'organization',   tKey: 'organization'   },
  position:        { key: 'position',       tKey: 'position'       },
  gender:          { key: 'gender',         tKey: 'gender'         },
  country:         { key: 'country',        tKey: 'country'        },
  city:            { key: 'city',           tKey: 'city'           },
  age:             { key: 'age',            tKey: 'age'            },
  id_number:       { key: 'idNumber',       tKey: 'idNumber'       },
  passport_number: { key: 'passportNumber', tKey: 'passportNumber' },
  blood_type:      { key: 'bloodType',      tKey: 'bloodType'      },
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function View(props: Props) {
  const { event, eventUser, registrationFields, customFields, urls, translations: t, ...shell } = props

  const visibleFields = registrationFields
    .filter((f) => FIELD_MAP[f])
    .map((f) => ({
      label: (t as Record<string, string>)[FIELD_MAP[f].tKey] ?? f,
      value: eventUser[FIELD_MAP[f].key],
    }))
    .filter((f) => f.value)

  const visibleCustom = customFields.filter((f) => f.value)

  function handleApprove() {
    router.visit(urls.approve, { preserveScroll: true })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.userDetails} — ${event.name}`} />

      <div className="mx-auto max-w-xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.eventUsers)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            {eventUser.imageUrl ? (
              <img
                src={eventUser.imageUrl}
                alt=""
                className="h-16 w-16 rounded-full object-cover border border-[#E8E8E5]"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F1F1EF]">
                <User className="h-7 w-7 text-[#91918E]" />
              </div>
            )}
            <div className="flex-1">
              <CardTitle className="text-[#37352F]">
                {[eventUser.firstName, eventUser.lastName].filter(Boolean).join(' ') || '—'}
              </CardTitle>
              <Badge
                variant={eventUser.approved ? 'default' : 'secondary'}
                className={
                  eventUser.approved
                    ? 'mt-1 bg-green-100 text-green-700 hover:bg-green-100'
                    : 'mt-1 bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                }
              >
                {eventUser.approved ? (
                  <><Check className="mr-1 h-3 w-3" />{t.approved}</>
                ) : (
                  <><Clock className="mr-1 h-3 w-3" />{t.pending}</>
                )}
              </Badge>
            </div>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-4">
            {visibleFields.map((f) => (
              <div key={f.label} className="flex items-start gap-2">
                <span className="w-36 shrink-0 text-sm text-[#91918E]">{f.label}</span>
                <span className="text-sm text-[#37352F]">{String(f.value)}</span>
              </div>
            ))}

            {visibleCustom.length > 0 && (
              <>
                <Separator className="my-2" />
                {visibleCustom.map((f) => (
                  <div key={f.name} className="flex items-start gap-2">
                    <span className="w-36 shrink-0 text-sm text-[#91918E]">{f.name}</span>
                    <span className="text-sm text-[#37352F]">{f.value}</span>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {!eventUser.approved && (
          <div className="mt-6 flex justify-end">
            <Button
              className="bg-[#2383E2] hover:bg-[#1b6ec2]"
              onClick={handleApprove}
            >
              <Check className="mr-2 h-4 w-4" />
              {t.approve}
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  )
}
